/**
 * GraphQL Nomination Service
 * Connects to AppSync API for nomination operations.
 * Mirrors the in-memory NominationService interface but uses real DynamoDB via AppSync.
 */

import { generateClient } from 'aws-amplify/api';
import {
  Nomination,
  NominationKind,
  NominationStatus,
  CreateNominationInput,
  UpdateNominationInput,
  ReviewerNote,
  ReviewerNoteInput,
  AuditEntry,
  FeedComment,
  TeamMember,
} from '../types/nomination';
import { uniqueTeamMembers, validateCreateNominationInput } from '../types/nominationValidation';
import { identityVariants } from './identity';

// Lazy client init
let client: any = null;
const getClient = () => {
  if (!client) client = generateClient();
  return client;
};

const getAuthMode = (): { authMode: 'userPool' } => ({ authMode: 'userPool' as const });
const getData = (result: any): any => ('data' in result ? result.data : null);

/**
 * Read a connection page from a resolved result OR a thrown Amplify error that
 * still carries a partial payload.
 *
 * AppSync nulls out any stored row that violates a non-null field in the schema
 * — a nomination saved with nomineeEmail but no nomineeId, for example — and
 * reports it as HTTP 200 with `items: [row, null, ...]` plus an errors array,
 * which Amplify v6 raises as a throw with the data on `error.data`.
 */
const extractPage = (resultOrError: any, field: string): { items: any[]; nextToken?: string } => {
  const page = resultOrError?.data?.[field];
  return {
    items: Array.isArray(page?.items) ? page.items : [],
    nextToken: page?.nextToken || undefined,
  };
};

/** Keep only the rows AppSync actually resolved. */
const liveRows = (rawItems: any[], context: string): any[] => {
  const usable = rawItems.filter(item => item && item.id);
  const nulled = rawItems.length - usable.length;
  if (nulled > 0) {
    console.warn(
      `[GraphQL Nomination] ${context}: ignored ${nulled} row(s) returned as null by AppSync ` +
      `— the stored item is missing a field the schema marks non-null`
    );
  }
  return usable;
};

/*
 * The approval attribution fields exist only once terraform has been applied.
 *
 * Adding approvedAt/approvedBy to the schema is backward compatible. Adding them to a QUERY is
 * not: AppSync validates the whole document against its deployed schema and rejects it outright
 * if a field is unknown, returning no data at all. collectPages then recovers zero rows from the
 * error payload, logs a warning, and hands back an empty list -- so asking for a field the
 * deployed schema does not have makes every nomination silently disappear, which is exactly what
 * happened between the frontend change and the terraform apply.
 *
 * Rather than depending on deploy order, the read path asks for the fields, and on that specific
 * rejection stops asking and retries immediately. One extra request per session before the
 * schema is deployed, none after, and the list never comes back empty because of it.
 */
let approvalFieldsSupported = true;

/** Matches the two field lines inside a selection set, and nothing else. */
const APPROVAL_FIELD_LINES = /^[ \t]*approved(?:At|By)[ \t]*\n/gm;

/*
 * The team fields are the same story, one release later.
 *
 * nominationType/teamName/teamMembers exist only once terraform has been applied, and naming an
 * undeployed field in a selection set does not return fewer columns — it rejects the whole
 * document and empties every nomination screen. A separate flag rather than one shared with the
 * approval fields, because the two can be deployed independently and a single flag would switch
 * off fields that are perfectly well supported.
 */
let teamFieldsSupported = true;

const TEAM_FIELD_LINES = /^[ \t]*(?:nominationType|teamName|teamMembers)[ \t]*\n/gm;

const isUnknownTeamField = (err: any): boolean => {
  if (!teamFieldsSupported) return false;
  const messages = [
    err?.message,
    ...((err?.errors || []).map((e: any) => e?.message)),
  ].filter(Boolean).join(' | ');
  return /nominationType|teamName|teamMembers/.test(messages);
};

const disableTeamFields = (context: string): void => {
  teamFieldsSupported = false;
  console.warn(
    `[GraphQL Nomination] ${context}: the deployed schema has no nominationType/teamName/` +
    'teamMembers — retrying without them. Apply terraform to store team nominations in full.'
  );
};

/** Whether this environment can persist a team nomination. Exported for the create path's guard. */
export const teamFieldsAreSupported = (): boolean => teamFieldsSupported;

const forDeployedSchema = (query: string): string => {
  const withoutApproval = approvalFieldsSupported ? query : query.replace(APPROVAL_FIELD_LINES, '');
  return teamFieldsSupported ? withoutApproval : withoutApproval.replace(TEAM_FIELD_LINES, '');
};

/** Either optional group being unknown is worth one silent retry; anything else is a real fault. */
const isUnknownOptionalField = (err: any): boolean =>
  isUnknownApprovalField(err) || isUnknownTeamField(err);

/** Turn off whichever group the rejection named, so the retry asks for strictly less. */
const disableRejectedFields = (err: any, context: string): void => {
  if (isUnknownApprovalField(err)) disableApprovalFields(context);
  if (isUnknownTeamField(err)) disableTeamFields(context);
};

/*
 * Only the approval fields being unknown counts. A different validation error is a real bug and
 * must keep surfacing rather than being silently retried into a narrower query.
 */
const isUnknownApprovalField = (err: any): boolean => {
  if (!approvalFieldsSupported) return false;
  const messages = [
    err?.message,
    ...((err?.errors || []).map((e: any) => e?.message)),
  ].filter(Boolean).join(' | ');
  return /approvedAt|approvedBy/.test(messages);
};

/** Records that this environment predates the field, so nothing asks for it again. */
const disableApprovalFields = (context: string): void => {
  approvalFieldsSupported = false;
  console.warn(
    `[GraphQL Nomination] ${context}: the deployed schema has no approvedAt/approvedBy — ` +
    'retrying without them. Apply terraform to record who approved each nomination.'
  );
};

/**
 * Walk every page of a connection query, keeping the rows that resolve. A failing
 * page ends the walk but still contributes whatever it managed to return.
 */
const collectPages = async (
  query: string,
  field: string,
  variables: Record<string, any>,
  context: string
): Promise<any[]> => {
  const collected: any[] = [];
  let nextToken: string | undefined = undefined;

  do {
    let page: { items: any[]; nextToken?: string };
    // Captured per iteration: nextToken is reassigned at the foot of the loop, and a closure
    // reading the mutable binding would send the wrong cursor if it ever outlived its turn.
    const pageToken = nextToken;
    const fetchPage = async () => {
      const result: any = await getClient().graphql({
        query: forDeployedSchema(query),
        variables: { ...variables, nextToken: pageToken },
        ...getAuthMode(),
      } as any);
      if (result?.errors?.length) {
        /*
         * Amplify surfaces GraphQL errors either by throwing or by resolving with an errors
         * array, depending on the failure. A validation error that arrives on THIS path used to
         * be logged as "partial errors" and then reduced to zero rows by extractPage, so the
         * retry below never ran and the list came back empty anyway. Rethrowing routes both
         * shapes through the same recovery.
         */
        if (isUnknownOptionalField(result)) throw result;
        console.warn(`[GraphQL Nomination] ${context}: partial errors`, JSON.stringify(result.errors));
      }
      return extractPage(result, field);
    };
    try {
      try {
        page = await fetchPage();
      } catch (firstError: any) {
        // The one retry worth making: the document was rejected for naming a field this
        // environment does not have yet, so it returned nothing rather than fewer columns.
        if (!isUnknownOptionalField(firstError)) throw firstError;
        disableRejectedFields(firstError, context);
        page = await fetchPage();
      }
    } catch (pageError: any) {
      page = extractPage(pageError, field);
      const recovered = liveRows(page.items, context);
      console.warn(
        `[GraphQL Nomination] ${context}: page failed ` +
        `(${pageError?.errors?.[0]?.message || pageError?.message || pageError}); ` +
        `recovered ${recovered.length} row(s)`
      );
      collected.push(...recovered);
      break;
    }
    collected.push(...liveRows(page.items, context));
    nextToken = page.nextToken;
  } while (nextToken);

  return collected;
};

/** Map rows to Nominations, dropping any single row that fails to map. */
const mapNominationList = (rawItems: any[], context: string): Nomination[] => {
  const byId = new Map<string, any>();
  rawItems.forEach(item => { if (item?.id) byId.set(item.id, item); });
  return Array.from(byId.values())
    .map(item => {
      try {
        return mapNomination(item);
      } catch (err) {
        console.error(`[GraphQL Nomination] ${context}: could not map row`, item?.id, err);
        return null;
      }
    })
    .filter((n): n is Nomination => n !== null);
};

/**
 * Legacy spellings that predate the current status vocabulary. Kept in step with
 * normalizeStatus: a value that normalizes into a status must also be queried for
 * it, or the row is normalized correctly on the scan path but never fetched on the
 * GSI path.
 */
const LEGACY_STATUS_ALIASES: Partial<Record<NominationStatus, string[]>> = {
  [NominationStatus.SUBMITTED]: ['PENDING', 'Pending'],
};

/**
 * Status values in DynamoDB are not consistently cased — the same state exists as
 * both 'Submitted' and 'SUBMITTED' in stored data. GSI queries are exact-match and
 * case-sensitive, so every stored spelling has to be asked for explicitly or those
 * rows stay invisible in the UI while sitting in the table.
 */
const statusQueryVariants = (status: NominationStatus): string[] => {
  const variants = new Set<string>([
    status,
    status.toUpperCase().replace(/ /g, '_'),
    status.toUpperCase(),
    ...(LEGACY_STATUS_ALIASES[status] || []),
  ]);
  return Array.from(variants);
};

const NOMINATION_CACHE_TTL_MS = 30000;
type NominationCacheEntry = { timestamp: number; data: Nomination[] };
const nominationListCache = new Map<string, NominationCacheEntry>();

function getCachedNominations(cacheKey: string): Nomination[] | null {
  const cached = nominationListCache.get(cacheKey);
  if (!cached || Date.now() - cached.timestamp > NOMINATION_CACHE_TTL_MS) return null;
  return cached.data;
}

function setCachedNominations(cacheKey: string, data: Nomination[]): void {
  nominationListCache.set(cacheKey, { timestamp: Date.now(), data });
}

function clearNominationCaches(): void {
  nominationListCache.clear();
}

// ---- GraphQL Queries ----

// NOTE: nomineePhotoUrl and feedComments fields are now included.
// Requires the updated AppSync schema deployed via Terraform.

const listNominationsQuery = /* GraphQL */ `
  query ListNominations($limit: Int, $nextToken: String) {
    listNominations(limit: $limit, nextToken: $nextToken) {
      items {
        id
        nomineeId
        nomineeName
        nominationType
        teamName
        teamMembers
        nominatorId
        nominatorName
        awardType
        reason
        supportingDetails
        nomineePhotoUrl
        status
        rejectionReason
        publishedAt
        publishedBy
        approvedAt
        approvedBy
        createdAt
        updatedAt
        reviewerNotes {
          id
          nominationId
          reviewerId
          reviewerName
          content
          createdAt
        }
        feedComments {
          id
          nominationId
          authorId
          authorName
          content
          createdAt
        }
      }
      nextToken
    }
  }
`;

const getNominationQuery = /* GraphQL */ `
  query GetNomination($id: ID!) {
    getNomination(id: $id) {
      id
      nomineeId
      nomineeName
      nominationType
      teamName
      teamMembers
      nominatorId
      nominatorName
      awardType
      reason
      supportingDetails
      nomineePhotoUrl
      status
      rejectionReason
      publishedAt
      publishedBy
      approvedAt
      approvedBy
      createdAt
      updatedAt
      reviewerNotes {
        id
        nominationId
        reviewerId
        reviewerName
        content
        createdAt
      }
      feedComments {
        id
        nominationId
        authorId
        authorName
        content
        createdAt
      }
    }
  }
`;

const nominationsByNominatorQuery = /* GraphQL */ `
  query NominationsByNominator($nominatorId: String!, $limit: Int, $nextToken: String) {
    nominationsByNominator(nominatorId: $nominatorId, limit: $limit, nextToken: $nextToken) {
      items {
        id
        nomineeId
        nomineeName
        nominationType
        teamName
        teamMembers
        nominatorId
        nominatorName
        awardType
        reason
        supportingDetails
        nomineePhotoUrl
        status
        rejectionReason
        publishedAt
        publishedBy
        approvedAt
        approvedBy
        createdAt
        updatedAt
      }
      nextToken
    }
  }
`;

const nominationsByStatusQuery = /* GraphQL */ `
  query NominationsByStatus($status: String!, $limit: Int, $nextToken: String) {
    nominationsByStatus(status: $status, limit: $limit, nextToken: $nextToken) {
      items {
        id
        nomineeId
        nomineeName
        nominationType
        teamName
        teamMembers
        nominatorId
        nominatorName
        awardType
        reason
        supportingDetails
        nomineePhotoUrl
        status
        rejectionReason
        publishedAt
        publishedBy
        approvedAt
        approvedBy
        createdAt
        updatedAt
        reviewerNotes {
          id
          nominationId
          reviewerId
          reviewerName
          content
          createdAt
        }
        feedComments {
          id
          nominationId
          authorId
          authorName
          content
          createdAt
        }
      }
      nextToken
    }
  }
`;

const nominationAuditByNominationQuery = /* GraphQL */ `
  query NominationAuditByNomination($nominationId: ID!, $limit: Int) {
    nominationAuditByNomination(nominationId: $nominationId, limit: $limit) {
      items {
        id
        nominationId
        action
        performedBy
        performedByName
        details
        timestamp
      }
    }
  }
`;

// ---- GraphQL Mutations ----

const createNominationMutation = /* GraphQL */ `
  mutation CreateNomination($input: CreateNominationInput!) {
    createNomination(input: $input) {
      id
      nomineeId
      nomineeName
      nominationType
      teamName
      teamMembers
      nominatorId
      nominatorName
      awardType
      reason
      supportingDetails
      nomineePhotoUrl
      status
      createdAt
      updatedAt
    }
  }
`;

const updateNominationMutation = /* GraphQL */ `
  mutation UpdateNomination($input: UpdateNominationInput!) {
    updateNomination(input: $input) {
      id
      nomineeId
      nomineeName
      nominationType
      teamName
      teamMembers
      nominatorId
      nominatorName
      awardType
      reason
      supportingDetails
      nomineePhotoUrl
      status
      rejectionReason
      publishedAt
      publishedBy
      approvedAt
      approvedBy
      createdAt
      updatedAt
    }
  }
`;

const deleteNominationMutation = /* GraphQL */ `
  mutation DeleteNomination($id: ID!) {
    deleteNomination(id: $id) {
      id
    }
  }
`;

const createReviewerNoteMutation = /* GraphQL */ `
  mutation CreateReviewerNote($input: CreateReviewerNoteInput!) {
    createReviewerNote(input: $input) {
      id
      nominationId
      reviewerId
      reviewerName
      content
      createdAt
    }
  }
`;

const createNominationAuditMutation = /* GraphQL */ `
  mutation CreateNominationAudit($input: CreateNominationAuditInput!) {
    createNominationAudit(input: $input) {
      id
      nominationId
      action
      performedBy
      performedByName
      details
      timestamp
    }
  }
`;

const createFeedCommentMutation = /* GraphQL */ `
  mutation CreateFeedComment($input: CreateFeedCommentInput!) {
    createFeedComment(input: $input) {
      id
      nominationId
      authorId
      authorName
      content
      createdAt
    }
  }
`;

const deleteFeedCommentMutation = /* GraphQL */ `
  mutation DeleteFeedComment($id: ID!) {
    deleteFeedComment(id: $id) {
      id
    }
  }
`;

const feedCommentsByNominationQuery = /* GraphQL */ `
  query FeedCommentsByNomination($nominationId: ID!, $limit: Int) {
    feedCommentsByNomination(nominationId: $nominationId, limit: $limit) {
      items {
        id
        nominationId
        authorId
        authorName
        content
        createdAt
      }
    }
  }
`;

// ---- Helper: normalize status (handles both "Submitted" and "SUBMITTED") ----
function normalizeStatus(rawStatus: string): NominationStatus {
  const statusMap: Record<string, NominationStatus> = {
    'SUBMITTED': NominationStatus.SUBMITTED,
    'Submitted': NominationStatus.SUBMITTED,
    'DRAFT': NominationStatus.DRAFT,
    'Draft': NominationStatus.DRAFT,
    'UNDER_REVIEW': NominationStatus.UNDER_REVIEW,
    'Under Review': NominationStatus.UNDER_REVIEW,
    'APPROVED': NominationStatus.APPROVED,
    'Approved': NominationStatus.APPROVED,
    'REJECTED': NominationStatus.REJECTED,
    'Rejected': NominationStatus.REJECTED,
    'PUBLISHED': NominationStatus.PUBLISHED,
    'Published': NominationStatus.PUBLISHED,
    // Legacy value written before the status vocabulary settled. Without a mapping
    // it matches no UI filter, so the row renders nowhere.
    'PENDING': NominationStatus.SUBMITTED,
    'Pending': NominationStatus.SUBMITTED,
  };
  return statusMap[rawStatus] || (rawStatus as NominationStatus);
}

/**
 * The team roster, stored as a JSON string.
 *
 * A string rather than a GraphQL list type because it keeps the stored row a flat DynamoDB item
 * and needs no second input type — the same trade-off `supportingDetails` already makes for the
 * award forms, and the same one it has to survive: the value is whatever was written, so a
 * malformed one must not take the nomination down with it.
 *
 * Anything unparseable, or parseable but not a list of {id, name}, comes back undefined. The
 * detail view then shows the team by name with no roster, which is the honest reading of a
 * damaged value and leaves every other field on the nomination usable.
 */
function parseTeamMembers(raw: any): TeamMember[] | undefined {
  if (!raw) return undefined;
  if (Array.isArray(raw)) return normalizeMembers(raw);
  if (typeof raw !== 'string') return undefined;
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? normalizeMembers(parsed) : undefined;
  } catch {
    console.warn('[GraphQL Nomination] teamMembers was not valid JSON; showing the team without a roster');
    return undefined;
  }
}

function normalizeMembers(list: any[]): TeamMember[] | undefined {
  const members = list
    .filter(entry => entry && typeof entry === 'object')
    .map(entry => ({ id: String(entry.id || ''), name: String(entry.name || '') }))
    .filter(entry => entry.id || entry.name);
  return members.length > 0 ? uniqueTeamMembers(members) : undefined;
}

// ---- Helper: map raw GraphQL item to Nomination ----

function mapNomination(item: any): Nomination {
  if (!item || !item.id) {
    throw new Error('No nomination data');
  }
  return {
    id: item.id,
    nomineeId: item.nomineeId || '',
    nomineeName: item.nomineeName || '',
    /*
     * Individual unless the row says otherwise.
     *
     * Covers three cases with one rule: a row written before teams existed, a row read from an
     * environment where the schema is not deployed yet (the fields are stripped from the query),
     * and a genuine individual nomination. None of them should render as a team with no members.
     */
    nominationType: item.nominationType === NominationKind.TEAM
      ? NominationKind.TEAM
      : NominationKind.INDIVIDUAL,
    teamName: item.teamName || undefined,
    teamMembers: parseTeamMembers(item.teamMembers),
    nominatorId: item.nominatorId || '',
    nominatorName: item.nominatorName || '',
    awardType: item.awardType || '',
    reason: item.reason || '',
    supportingDetails: item.supportingDetails || undefined,
    nomineePhotoUrl: item.nomineePhotoUrl || undefined,
    status: normalizeStatus(item.status || ''),
    reviewerNotes: (
      Array.isArray(item.reviewerNotes)
        ? item.reviewerNotes
        : item.reviewerNotes?.items || []
    ).map((n: any) => ({
      id: n.id,
      nominationId: n.nominationId,
      reviewerId: n.reviewerId,
      reviewerName: n.reviewerName,
      content: n.content,
      createdAt: n.createdAt,
    })),
    feedComments: (
      Array.isArray(item.feedComments)
        ? item.feedComments
        : item.feedComments?.items || []
    ).map((c: any) => ({
      id: c.id,
      nominationId: c.nominationId,
      authorId: c.authorId,
      authorName: c.authorName,
      content: c.content,
      createdAt: c.createdAt,
    })),
    rejectionReason: item.rejectionReason || undefined,
    publishedAt: item.publishedAt || undefined,
    publishedBy: item.publishedBy || undefined,
    approvedAt: item.approvedAt || undefined,
    approvedBy: item.approvedBy || undefined,
    createdAt: item.createdAt || new Date().toISOString(),
    updatedAt: item.updatedAt || new Date().toISOString(),
  };
}

// ---- Service Result Types (matching NominationService interface) ----

export interface PaginatedFeed {
  items: Nomination[];
  total: number;
  page: number;
  totalPages: number;
}

export interface NominationServiceResult {
  success: boolean;
  nomination?: Nomination;
  errors: string[];
}

const FEED_PAGE_SIZE = 10;

// ---- GraphQL Nomination Service ----

export class GraphQLNominationService {

  /** Create a nomination via AppSync. */
  static async createNomination(
    input: CreateNominationInput,
    nominatorId: string,
    nominatorName: string,
    asDraft: boolean = false
  ): Promise<NominationServiceResult> {
    const validation = validateCreateNominationInput(input);
    if (!validation.isValid) {
      return { success: false, errors: validation.errors };
    }

    try {
      const gqlInput: any = {
        nomineeId: input.nomineeId,
        nomineeName: input.nomineeName,
        nominatorId,
        nominatorName,
        awardType: input.awardType,
        reason: input.reason,
        supportingDetails: input.supportingDetails || '',
        status: asDraft ? NominationStatus.DRAFT : NominationStatus.SUBMITTED,
      };
      if (input.nomineePhotoUrl) {
        gqlInput.nomineePhotoUrl = input.nomineePhotoUrl;
      }
      /*
       * Sent only where the deployed schema can accept them.
       *
       * An unknown field on a mutation INPUT fails argument validation and loses the whole
       * write, so before the terraform apply a team nomination would not save at all. Dropping
       * the three fields stores it as an individual nomination named for the team — degraded,
       * but a saved nomination rather than an error the nominator cannot act on. The write path
       * cannot retry its way out of this the way a read can: a retry would risk creating the
       * row twice.
       */
      if (teamFieldsSupported && input.nominationType) {
        gqlInput.nominationType = input.nominationType;
        if (input.teamName) gqlInput.teamName = input.teamName;
        if (input.teamMembers?.length) {
          gqlInput.teamMembers = JSON.stringify(uniqueTeamMembers(input.teamMembers));
        }
      }

      const result = await getClient().graphql({
        query: forDeployedSchema(createNominationMutation),
        variables: { input: gqlInput },
        ...getAuthMode(),
      } as any);

      const data = getData(result);
      const nomination = mapNomination(data?.createNomination);
      clearNominationCaches();
      console.log('[GraphQL Nomination] Created:', nomination.id, nomination.status);
      return { success: true, nomination, errors: [] };
    } catch (error: any) {
      console.error('[GraphQL Nomination] Create error:', error);
      const msg = error?.errors?.[0]?.message || error?.message || 'Failed to create nomination';
      return { success: false, errors: [msg] };
    }
  }

  /** Update a nomination via AppSync. Records audit entry. */
  /*
   * Named for the decision the update represents. Falls back to EDITED for anything that is
   * not a status transition, which is what every update used to be logged as.
   */
  private static auditAction(updates: UpdateNominationInput): string {
    switch (updates.status) {
      case NominationStatus.APPROVED: return 'APPROVED';
      case NominationStatus.REJECTED: return 'REJECTED';
      case NominationStatus.PUBLISHED: return 'PUBLISHED';
      default: return 'EDITED';
    }
  }

  static async updateNomination(
    nominationId: string,
    updates: UpdateNominationInput,
    adminId: string,
    adminName: string
  ): Promise<NominationServiceResult> {
    try {
      const gqlInput: any = { id: nominationId };
      if (updates.nomineeName !== undefined) gqlInput.nomineeName = updates.nomineeName;
      if (updates.nomineeId !== undefined) gqlInput.nomineeId = updates.nomineeId;
      if (updates.awardType !== undefined) gqlInput.awardType = updates.awardType;
      if (updates.reason !== undefined) gqlInput.reason = updates.reason;
      if (updates.supportingDetails !== undefined) gqlInput.supportingDetails = updates.supportingDetails;
      if ((updates as any).nomineePhotoUrl !== undefined) gqlInput.nomineePhotoUrl = (updates as any).nomineePhotoUrl;
      if (updates.status !== undefined) gqlInput.status = updates.status;
      if ((updates as any).rejectionReason !== undefined) gqlInput.rejectionReason = (updates as any).rejectionReason;
      if ((updates as any).publishedAt !== undefined) gqlInput.publishedAt = (updates as any).publishedAt;
      if ((updates as any).publishedBy !== undefined) gqlInput.publishedBy = (updates as any).publishedBy;
      /*
       * Dropped entirely where the deployed schema has no such input field: sending it would fail
       * argument validation and lose the status change too, which matters far more than the
       * attribution. The approval still happens; only the record of who made it is lost, and the
       * queue already renders nothing rather than guessing when that value is absent.
       */
      if (approvalFieldsSupported) {
        if ((updates as any).approvedAt !== undefined) gqlInput.approvedAt = (updates as any).approvedAt;
        if ((updates as any).approvedBy !== undefined) gqlInput.approvedBy = (updates as any).approvedBy;
      }
      /* Same rule as the approval fields above, for the same reason. */
      if (teamFieldsSupported) {
        if (updates.nominationType !== undefined) gqlInput.nominationType = updates.nominationType;
        if (updates.teamName !== undefined) gqlInput.teamName = updates.teamName;
        if (updates.teamMembers !== undefined) {
          gqlInput.teamMembers = JSON.stringify(uniqueTeamMembers(updates.teamMembers));
        }
      }

      const runUpdate = () => getClient().graphql({
        query: forDeployedSchema(updateNominationMutation),
        variables: { input: gqlInput },
        ...getAuthMode(),
      } as any);

      let result: any;
      try {
        result = await runUpdate();
      } catch (updateError: any) {
        /*
         * One retry, with whichever group the schema rejected removed from BOTH the input and
         * the selection set. An update is idempotent — it names the row by id and sets fields —
         * so re-sending it cannot duplicate anything, which is why this can retry where create
         * deliberately does not.
         */
        if (!isUnknownOptionalField(updateError)) throw updateError;
        const wasApproval = isUnknownApprovalField(updateError);
        const wasTeam = isUnknownTeamField(updateError);
        disableRejectedFields(updateError, 'updateNomination');
        if (wasApproval) {
          delete gqlInput.approvedAt;
          delete gqlInput.approvedBy;
        }
        if (wasTeam) {
          delete gqlInput.nominationType;
          delete gqlInput.teamName;
          delete gqlInput.teamMembers;
        }
        result = await runUpdate();
      }

      const data = getData(result);
      const nomination = mapNomination(data?.updateNomination);
      clearNominationCaches();

      /*
       * The audit action names the decision.
       *
       * Every status change was logged as 'EDITED' with a details string listing which FIELDS
       * changed and not what they changed to, so an approval, a rejection and an unrelated
       * edit were indistinguishable in the trail. That is why the approver could not be
       * recovered after the fact even though their email was recorded on every one.
       *
       * A named action makes the entry answer 'who approved this', which the Ready to Publish
       * queue reads directly -- and it works against the schema as already deployed, so the
       * attribution does not wait on the approvedBy column being applied.
       */
      await GraphQLNominationService.createAuditEntry(
        nominationId,
        GraphQLNominationService.auditAction(updates),
        adminId,
        adminName,
        `Fields updated: ${Object.keys(updates).join(', ')}`
      );

      console.log('[GraphQL Nomination] Updated:', nominationId);
      return { success: true, nomination, errors: [] };
    } catch (error: any) {
      console.error('[GraphQL Nomination] Update error:', error);
      return { success: false, errors: [error.message || 'Failed to update nomination'] };
    }
  }

  /** Delete a nomination via AppSync. Records audit entry. */
  static async deleteNomination(
    nominationId: string,
    adminId: string,
    adminName: string
  ): Promise<{ success: boolean; errors: string[] }> {
    try {
      await getClient().graphql({
        query: deleteNominationMutation,
        variables: { id: nominationId },
        ...getAuthMode(),
      } as any);
      clearNominationCaches();

      await GraphQLNominationService.createAuditEntry(
        nominationId,
        'DELETED',
        adminId,
        adminName,
        `Nomination ${nominationId} deleted`
      );

      console.log('[GraphQL Nomination] Deleted:', nominationId);
      return { success: true, errors: [] };
    } catch (error: any) {
      console.error('[GraphQL Nomination] Delete error:', error);
      return { success: false, errors: [error.message || 'Failed to delete nomination'] };
    }
  }

  /** Get a single nomination by ID. */
  static async getNomination(nominationId: string): Promise<Nomination | undefined> {
    const runGet = () => getClient().graphql({
      query: forDeployedSchema(getNominationQuery),
      variables: { id: nominationId },
      ...getAuthMode(),
    } as any);

    try {
      let result: any;
      try {
        result = await runGet();
      } catch (firstError: any) {
        /*
         * The same one retry the list path makes. Without it, opening a nomination on an
         * environment that predates these fields returned undefined and the detail page said
         * the nomination did not exist — which is a worse lie than showing it without a roster.
         */
        if (!isUnknownOptionalField(firstError)) throw firstError;
        disableRejectedFields(firstError, 'getNomination');
        result = await runGet();
      }

      const data = getData(result);
      if (!data?.getNomination) return undefined;
      return mapNomination(data.getNomination);
    } catch (error) {
      console.error('[GraphQL Nomination] Get error:', error);
      return undefined;
    }
  }

  /** Get nominations created by a specific user (supports both UUID and email for backward compatibility). */
  static async getMyNominations(
    nominatorId: string,
    nominatorEmail?: string
  ): Promise<Nomination[]> {
    const startedAt = performance.now();
    let queryCount = 0;
    console.log('[Perf][MyNominations] GraphQL getMyNominations start', {
      nominatorId,
      nominatorEmail,
    });
    const allItems: any[] = [];

    // Helper to safely fetch nominations — handles partial errors from AppSync
    const fetchNominations = async (id: string): Promise<any[]> => {
      try {
        const queryStartedAt = performance.now();
        queryCount += 1;
        console.log('[Perf][MyNominations] GraphQL query start', {
          name: 'NominationsByNominator',
          nominatorId: id,
          limit: 100,
          queryCount,
        });
        const result = await getClient().graphql({
          query: forDeployedSchema(nominationsByNominatorQuery),
          variables: { nominatorId: id, limit: 100 },
          ...getAuthMode(),
        } as any);
        console.log('[Perf][MyNominations] GraphQL query complete', {
          name: 'NominationsByNominator',
          nominatorId: id,
          durationMs: Math.round(performance.now() - queryStartedAt),
        });
        const data = getData(result);
        return data?.nominationsByNominator?.items || [];
      } catch (err: any) {
        // AppSync returns partial data with errors — extract items from error object
        const partialData = err?.data?.nominationsByNominator?.items;
        if (partialData && Array.isArray(partialData)) {
          console.warn('[GraphQL Nomination] Partial success for', id, '- recovered', partialData.length, 'items');
          return partialData;
        }
        console.error('[GraphQL Nomination] Query failed for', id, err?.errors?.[0]?.message || err?.message);
        return [];
      }
    };

    // Widen across both email domains. Nominations written before the
    // creyentetech.com → creyenteinfotech.com move are keyed under the old address,
    // and every employee has a Cognito account on each domain — so whichever one they
    // signed in with, half their history sits under the other. Each id is also kept
    // verbatim, since GSI keys are case-sensitive and identityVariants() lowercases.
    const uniqueNominatorIds = Array.from(new Set(
      [nominatorId, nominatorEmail].flatMap(value => {
        const trimmed = (value || '').trim();
        return trimmed ? [trimmed, ...identityVariants(trimmed)] : [];
      })
    ));

    const nominationPages = await Promise.all(uniqueNominatorIds.map(id => fetchNominations(id)));
    nominationPages.forEach(items => allItems.push(...items));

    // 3. Remove duplicates
    const uniqueMap = new Map();
    allItems.forEach((item) => {
      if (item?.id) {
        uniqueMap.set(item.id, item);
      }
    });

    // 4. Map and sort — skip broken items gracefully
    const nominations = Array.from(uniqueMap.values())
      .filter((item: any) => item && item.id)
      .map((item: any) => {
        try {
          return mapNomination(item);
        } catch (err) {
          console.error('[Nomination Mapping Error]', item?.id, err);
          return null;
        }
      })
      .filter((n): n is Nomination => n !== null)
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

    console.log('[GraphQL Nomination] Loaded nominations:', nominations.length);
    console.log('[Perf][MyNominations] GraphQL getMyNominations complete', {
      durationMs: Math.round(performance.now() - startedAt),
      queryCount,
      rawItemCount: allItems.length,
      dedupedCount: nominations.length,
    });
    return nominations;
  }

  /** Get the review queue: Submitted or Under Review nominations. */
  static async getReviewQueue(): Promise<Nomination[]> {
    try {
      const cacheKey = 'reviewQueue';
      const cached = getCachedNominations(cacheKey);
      if (cached) return cached;
      const startedAt = performance.now();
      // Ask for every stored spelling of each status — the GSI is case-sensitive.
      const wanted = [NominationStatus.SUBMITTED, NominationStatus.UNDER_REVIEW]
        .flatMap(statusQueryVariants);
      console.log('[Perf][ReviewQueue] GraphQL getReviewQueue start', {
        queryCount: wanted.length,
        statuses: wanted,
      });

      const pages = await Promise.all(wanted.map(status => collectPages(
        nominationsByStatusQuery,
        'nominationsByStatus',
        { status, limit: 100 },
        `getReviewQueue(${status})`
      )));

      // mapNominationList dedupes by id, so overlapping status spellings are safe.
      const nominations = mapNominationList(pages.flat(), 'getReviewQueue');
      console.log('[Perf][ReviewQueue] GraphQL getReviewQueue complete', {
        durationMs: Math.round(performance.now() - startedAt),
        queryCount: wanted.length,
        rawItemCount: pages.flat().length,
        count: nominations.length,
      });
      setCachedNominations(cacheKey, nominations);
      return nominations;
    } catch (error) {
      console.error('[GraphQL Nomination] getReviewQueue error:', error);
      return [];
    }
  }

  /** Get published nominations feed, sorted by publishedAt desc, paginated. */
  static async getFeed(page: number = 1): Promise<PaginatedFeed> {
    try {
      // Every stored spelling of PUBLISHED, and every page of each — the previous
      // single un-paginated call silently capped the whole feed at one page.
      const pages = await Promise.all(
        statusQueryVariants(NominationStatus.PUBLISHED).map(status => collectPages(
          nominationsByStatusQuery,
          'nominationsByStatus',
          { status, limit: 100 },
          `getFeed(${status})`
        ))
      );

      const items: Nomination[] = mapNominationList(pages.flat(), 'getFeed')
        .sort((a: Nomination, b: Nomination) => {
          const dateA = a.publishedAt || '';
          const dateB = b.publishedAt || '';
          return dateB.localeCompare(dateA);
        });

      const total = items.length;
      const totalPages = Math.max(1, Math.ceil(total / FEED_PAGE_SIZE));
      const safePage = Math.max(1, Math.min(page, totalPages));
      const start = (safePage - 1) * FEED_PAGE_SIZE;
      const pageItems = items.slice(start, start + FEED_PAGE_SIZE);

      return { items: pageItems, total, page: safePage, totalPages };
    } catch (error) {
      console.error('[GraphQL Nomination] getFeed error:', error);
      return { items: [], total: 0, page: 1, totalPages: 1 };
    }
  }

  /** Get all nominations regardless of status (admin view). */
  static async getAllNominations(): Promise<Nomination[]> {
    try {
      const cacheKey = 'allNominations';
      const cached = getCachedNominations(cacheKey);
      if (cached) return cached;
      const startedAt = performance.now();
      console.log('[Perf][ManageNominations] GraphQL getAllNominations start', {
        limit: 100,
      });

      const items = await collectPages(
        listNominationsQuery,
        'listNominations',
        { limit: 100 },
        'getAllNominations'
      );

      const nominations = mapNominationList(items, 'getAllNominations');
      console.log('[Perf][ManageNominations] GraphQL getAllNominations complete', {
        durationMs: Math.round(performance.now() - startedAt),
        rawItemCount: items.length,
        count: nominations.length,
        hasNextToken: false,
      });
      setCachedNominations(cacheKey, nominations);
      return nominations;
    } catch (error) {
      console.error('[GraphQL Nomination] getAllNominations error:', error);
      return [];
    }
  }

  /** Alias used by ReviewQueue to get raw list. */
  static async getAllNominationsRaw(): Promise<Nomination[]> {
    return GraphQLNominationService.getAllNominations();
  }

  /** Add a reviewer note to a nomination. */
  static async addReviewerNote(
    input: ReviewerNoteInput,
    reviewerId: string,
    reviewerName: string
  ): Promise<{ success: boolean; note?: ReviewerNote; errors: string[] }> {
    if (!input.content || input.content.trim().length === 0) {
      return { success: false, errors: ['Note content is required'] };
    }

    try {
      const result = await getClient().graphql({
        query: createReviewerNoteMutation,
        variables: {
          input: {
            nominationId: input.nominationId,
            reviewerId,
            reviewerName,
            content: input.content,
          },
        },
        ...getAuthMode(),
      } as any);

      const data = getData(result);
      const note: ReviewerNote = {
        id: data?.createReviewerNote?.id,
        nominationId: data?.createReviewerNote?.nominationId,
        reviewerId: data?.createReviewerNote?.reviewerId,
        reviewerName: data?.createReviewerNote?.reviewerName,
        content: data?.createReviewerNote?.content,
        createdAt: data?.createReviewerNote?.createdAt,
      };

      console.log('[GraphQL Nomination] Note added:', note.id);
      return { success: true, note, errors: [] };
    } catch (error: any) {
      console.error('[GraphQL Nomination] addReviewerNote error:', error);
      return { success: false, errors: [error.message || 'Failed to add note'] };
    }
  }

  /** Create an audit entry. */
  static async createAuditEntry(
    nominationId: string,
    action: string,
    performedBy: string,
    performedByName: string,
    details: string
  ): Promise<void> {
    try {
      await getClient().graphql({
        query: createNominationAuditMutation,
        variables: {
          input: { nominationId, action, performedBy, performedByName, details },
        },
        ...getAuthMode(),
      } as any);
    } catch (error) {
      console.warn('[GraphQL Nomination] Audit entry creation failed:', error);
    }
  }

  /** Get audit entries for a nomination. */
  static async getAuditEntries(nominationId: string): Promise<AuditEntry[]> {
    try {
      const result = await getClient().graphql({
        query: nominationAuditByNominationQuery,
        variables: { nominationId, limit: 100 },
        ...getAuthMode(),
      } as any);

      const data = getData(result);
      return (data?.nominationAuditByNomination?.items || []).map((item: any) => ({
        id: item.id,
        nominationId: item.nominationId,
        action: item.action,
        performedBy: item.performedBy,
        performedByName: item.performedByName,
        details: item.details,
        timestamp: item.timestamp,
      }));
    } catch (error) {
      console.error('[GraphQL Nomination] getAuditEntries error:', error);
      return [];
    }
  }

  /** Create a feed comment on a published nomination. */
  static async createFeedComment(
    nominationId: string,
    authorId: string,
    authorName: string,
    content: string
  ): Promise<{ success: boolean; comment?: FeedComment; errors: string[] }> {
    if (!content || content.trim().length === 0) {
      return { success: false, errors: ['Comment content is required'] };
    }
    try {
      const result = await getClient().graphql({
        query: createFeedCommentMutation,
        variables: {
          input: { nominationId, authorId, authorName, content: content.trim() },
        },
        ...getAuthMode(),
      } as any);

      const data = getData(result);
      const comment: FeedComment = {
        id: data?.createFeedComment?.id,
        nominationId: data?.createFeedComment?.nominationId,
        authorId: data?.createFeedComment?.authorId,
        authorName: data?.createFeedComment?.authorName,
        content: data?.createFeedComment?.content,
        createdAt: data?.createFeedComment?.createdAt,
      };
      return { success: true, comment, errors: [] };
    } catch (error: any) {
      console.error('[GraphQL Nomination] createFeedComment error:', error);
      return { success: false, errors: [error.message || 'Failed to add comment'] };
    }
  }

  /** Get feed comments for a nomination. */
  static async getFeedComments(nominationId: string): Promise<FeedComment[]> {
    try {
      const result = await getClient().graphql({
        query: feedCommentsByNominationQuery,
        variables: { nominationId, limit: 100 },
        ...getAuthMode(),
      } as any);

      const data = getData(result);
      return (data?.feedCommentsByNomination?.items || []).map((c: any) => ({
        id: c.id,
        nominationId: c.nominationId,
        authorId: c.authorId,
        authorName: c.authorName,
        content: c.content,
        createdAt: c.createdAt,
      }));
    } catch (error) {
      console.error('[GraphQL Nomination] getFeedComments error:', error);
      return [];
    }
  }

  /** Delete a feed comment. */
  static async deleteFeedComment(commentId: string): Promise<{ success: boolean; errors: string[] }> {
    try {
      await getClient().graphql({
        query: deleteFeedCommentMutation,
        variables: { id: commentId },
        ...getAuthMode(),
      } as any);
      return { success: true, errors: [] };
    } catch (error: any) {
      return { success: false, errors: [error.message || 'Failed to delete comment'] };
    }
  }
}
